import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { ArrowLeft, MapPin, Sparkles, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ShareButtons from '../components/ShareButtons';
import ImageLightbox from '../components/ImageLightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [wish, setWish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const it = language === 'it';

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/wishes/${id}/detail`);
        setWish(res.data);
        document.title = res.data.title + ' - Desideri del Cuore Moneyfor';
      } catch { navigate('/heart-desires'); }
      finally { setLoading(false); }
    };
    fetch();
    return () => { document.title = 'Moneyfor'; };
  }, [id]);

  const handleChat = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/wishes/${id}/contact`, {}, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/chat', { state: { conversationId: res.data.conversation_id } });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="min-h-screen bg-stone-100 dark:bg-stone-950"><Navbar /><div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" /></div></div>;
  if (!wish) return null;

  const isOwner = user?.user_id === wish.user_id;
  const shareUrl = `${API_URL}/api/share/wish/${id}`;

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950 overflow-y-auto">
      <Navbar />
      {lightboxIdx >= 0 && <ImageLightbox images={wish.images} initialIndex={lightboxIdx} onClose={() => setLightboxIdx(-1)} />}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-16">
        <Link to="/heart-desires" className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-700 mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" />{it ? 'Torna ai Desideri' : 'Back to Wishes'}
        </Link>

        <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-sm border border-stone-200 dark:border-stone-700 overflow-hidden">
          {wish.images?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {wish.images.map((img, i) => (
                <div key={i} className={`${i === 0 && wish.images.length > 1 ? 'md:row-span-2' : ''} overflow-hidden cursor-pointer`}
                  onClick={() => setLightboxIdx(i)}>
                  <img src={img} alt={wish.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" style={{ minHeight: i === 0 ? '300px' : '150px', maxHeight: i === 0 ? '500px' : '250px' }} />
                </div>
              ))}
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Badge className="bg-rose-100 text-rose-700"><Sparkles className="h-3 w-3 mr-1" />{wish.category}</Badge>
              {wish.city && <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{wish.city}</Badge>}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4" data-testid="wish-detail-title">{wish.title}</h1>
            <p className="text-stone-600 dark:text-stone-400 leading-relaxed mb-6 whitespace-pre-wrap">{wish.description}</p>

            <div className="flex items-center justify-between pt-4 border-t border-stone-200 dark:border-stone-700">
              <Link to={`/profile/${wish.user_id}`} className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={wish.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${wish.user_id}`} />
                  <AvatarFallback>{wish.user_name?.[0]}</AvatarFallback>
                </Avatar>
                <p className="font-semibold text-stone-900 dark:text-white flex items-center gap-1.5">{wish.user_name} {wish.is_verified && <CheckCircle className="h-4 w-4 text-blue-500" />}</p>
              </Link>
              <div className="flex items-center gap-3">
                <ShareButtons url={shareUrl} title={wish.title} description={wish.description} language={language} />
                {isAuthenticated && !isOwner && (
                  <Button onClick={handleChat} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-6" data-testid="chat-with-owner">
                    <MessageCircle className="h-4 w-4 mr-2" />{it ? 'Realizza il desiderio' : 'Fulfill wish'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WishDetail;
